import { useEffect } from "react";
import type { TrackingConfig } from "#/_packages/umbrella/utils/link-tracking";

type AnalyticsWindow = Window & {
	umbrellaTracking?: TrackingConfig;
	plausible?: (...args: unknown[]) => void;
	Cookiebot?: { consent?: { statistics?: boolean } };
};

/** Site-owned analytics installation and consent. Umbrella owns event initialization. */
export function initGsaAnalytics(doc: Document): () => void {
	const view = doc.defaultView as AnalyticsWindow | null;
	if (!view) return () => {};
	const previous = view.umbrellaTracking;
	const production = /^(www\.)?gsakassa\.se$/i.test(view.location.hostname);
	const allowed = () => {
		try {
			return (
				(!view.Cookiebot || view.Cookiebot.consent?.statistics === true) &&
				(previous?.isAllowed?.("plausible") ?? true)
			);
		} catch {
			// An unavailable consent integration must not break public rendering.
			return false;
		}
	};
	const config: TrackingConfig = {
		...previous,
		languageEvent:
			previous?.languageEvent ?? (production ? undefined : "Test01"),
		providers: [
			...new Set([...(previous?.providers ?? []), "plausible" as const]),
		],
		isAllowed: (provider) =>
			provider === "plausible"
				? allowed()
				: (previous?.isAllowed?.(provider) ?? true),
	};
	view.umbrellaTracking = config;

	const loadPlausible = () => {
		if (
			!allowed() ||
			typeof view.plausible === "function" ||
			doc.querySelector(
				'script[data-domain="gsakassa.se"][src^="https://plausible.io/"]',
			)
		)
			return;
		const script = doc.createElement("script");
		// The test goal belongs to this existing site. Non-production must not emit pageviews.
		script.src = production
			? "https://plausible.io/js/script.js"
			: "https://plausible.io/js/script.manual.js";
		script.defer = true;
		script.setAttribute("data-domain", "gsakassa.se");
		script.setAttribute("data-gsa-analytics", "");
		doc.head.appendChild(script);
	};
	loadPlausible();
	view.addEventListener("CookiebotOnConsentReady", loadPlausible);
	view.addEventListener("CookiebotOnAccept", loadPlausible);
	return () => {
		view.removeEventListener("CookiebotOnConsentReady", loadPlausible);
		view.removeEventListener("CookiebotOnAccept", loadPlausible);
		if (view.umbrellaTracking === config) view.umbrellaTracking = previous;
	};
}

export function GsaAnalytics() {
	useEffect(() => initGsaAnalytics(document), []);
	return null;
}
